// Country geography + scriptural-language groupings used by the
// CountryPicker bifurcation tabs ("By region" / "By language").
//
// Region is a static mapping (countries don't drift between continents).
// Language groups are derived from each country's `languages` array on
// `zakatConfig.countries` — co-official languages cause a country to
// appear in MULTIPLE language groups, which is intentional (Morocco
// shows up under both Arabic and French, etc.).

export const REGIONS = [
  { id: 'all',      label: 'All countries' },
  { id: 'mena',     label: 'Middle East & N. Africa' },
  { id: 'africa',   label: 'Sub-Saharan Africa' },
  { id: 'sasia',    label: 'South Asia' },
  { id: 'seasia',   label: 'Southeast Asia' },
  { id: 'casia',    label: 'Central Asia & Caucasus' },
  { id: 'easia',    label: 'East Asia' },
  { id: 'balkans',  label: 'Balkans' },
  { id: 'europe',   label: 'Europe' },
  { id: 'oceania',  label: 'Oceania' },
  { id: 'americas', label: 'Americas' },
];

// ISO-2 → region id. Keep in sync with zakatConfig.countries.
const REGION_OF = {
  // MENA (Middle East + North Africa)
  SA: 'mena', AE: 'mena', EG: 'mena', MA: 'mena', DZ: 'mena', TN: 'mena',
  LY: 'mena', SD: 'mena', IQ: 'mena', JO: 'mena', LB: 'mena', SY: 'mena',
  PS: 'mena', YE: 'mena', KW: 'mena', QA: 'mena', BH: 'mena', OM: 'mena',
  MR: 'mena', TR: 'mena', DJ: 'mena',
  // Sub-Saharan Africa
  SN: 'africa', ML: 'africa', NE: 'africa', BF: 'africa', CI: 'africa',
  TD: 'africa', CM: 'africa', GN: 'africa', BJ: 'africa', KM: 'africa',
  NG: 'africa', KE: 'africa', TZ: 'africa', UG: 'africa', ET: 'africa',
  SO: 'africa', ZA: 'africa',
  // South Asia
  IN: 'sasia', PK: 'sasia', BD: 'sasia', AF: 'sasia', LK: 'sasia', MV: 'sasia',
  // Southeast Asia
  MY: 'seasia', ID: 'seasia', BN: 'seasia', PH: 'seasia', TH: 'seasia', MM: 'seasia',
  // Central Asia + Caucasus
  KZ: 'casia', UZ: 'casia', KG: 'casia', TJ: 'casia', AZ: 'casia',
  TM: 'casia', CY: 'casia', IR: 'casia',
  // East Asia
  CN: 'easia', JP: 'easia',
  // Europe (extended Feb 2026 + Mar 2026)
  DE: 'europe', IT: 'europe', ES: 'europe', NL: 'europe', CH: 'europe',
  AT: 'europe', SE: 'europe', DK: 'europe', NO: 'europe',
  PL: 'europe', PT: 'europe', IE: 'europe', FI: 'europe',
  GR: 'europe', CZ: 'europe', LU: 'europe',
  // Balkans
  AL: 'balkans', BA: 'balkans', XK: 'balkans', MK: 'balkans',
  ME: 'balkans', RS: 'balkans', HR: 'balkans', SI: 'balkans', BG: 'balkans',
  // Europe
  GB: 'europe', FR: 'europe', RU: 'europe', BE: 'europe',
  // Oceania
  AU: 'oceania', NZ: 'oceania',
  // Southeast Asia additions
  SG: 'seasia',
  // Americas
  US: 'americas', CA: 'americas', BR: 'americas',
};

export const regionOf = (code) => REGION_OF[code] || 'all';

// Language groups shown in the "By language" tab. Order matters — the
// most-used scripts/languages come first.
export const LANGUAGE_GROUPS = [
  { id: 'all', label: 'All languages',   matches: () => true },
  { id: 'ar',  label: 'العربية · Arabic',  matches: (c) => c.languages.includes('ar') },
  { id: 'fr',  label: 'Français · French', matches: (c) => c.languages.includes('fr') },
  { id: 'en',  label: 'English',           matches: (c) => c.languages.includes('en') },
  { id: 'ur',  label: 'اردو · Urdu',       matches: (c) => c.languages.includes('ur') },
  { id: 'tr',  label: 'Türkçe · Turkish',  matches: (c) => c.languages.includes('tr') },
  { id: 'ru',  label: 'Русский · Russian', matches: (c) => c.languages.includes('ru') },
  { id: 'ms',  label: 'Melayu · Malay',    matches: (c) => c.languages.includes('ms') },
  { id: 'hi',  label: 'हिन्दी · Hindi',      matches: (c) => c.languages.includes('hi') },
  { id: 'bn',  label: 'বাংলা · Bengali',    matches: (c) => c.languages.includes('bn') },
  { id: 'ml',  label: 'മലയാളം · Malayalam', matches: (c) => c.languages.includes('ml') },
  { id: 'id',  label: 'Bahasa Indonesia',   matches: (c) => c.languages.includes('id') },
];
